import { useParams, Link } from 'react-router-dom';
import SeoHelmet from '@/components/feature/SeoHelmet';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, categories, articles } from '@/mocks/data';

function formatViews(num: number): string {
  if (num >= 10000) {
    return `${(num / 10000).toFixed(1)}万`;
  }
  return num.toLocaleString();
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:underline">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const ctaImage =
    'https://readdy.ai/api/search-image?query=Dark%20press%20conference%20stage%20with%20dramatic%20red%20spotlights%2C%20empty%20podium%20with%20microphones%2C%20cinematic%20journalism%20atmosphere%2C%20high%20contrast%20black%20and%20red%2C%20editorial%20photography%20style&width=1920&height=600&seq=cta-category&orientation=landscape';

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${category.name} - ${siteInfo.name}`,
      description: category.description,
      url: `${siteInfo.url}/category/${category.slug}`,
      isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: siteInfo.url },
      about: { '@type': 'Thing', name: category.name },
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHelmet
        title={`${category.name} - ${siteInfo.name}`}
        description={category.description}
        keywords={`${category.name},91吃瓜,91爆料,91黑料,吃瓜${category.name}`}
        canonicalPath={`/category/${category.slug}`}
        schemas={schemas}
      />
      <Navbar />

      <div className="pt-20 md:pt-24">
        <Breadcrumb
          items={[{ label: category.name, href: `/category/${category.slug}` }]}
        />
      </div>

      {/* Category Header */}
      <section className="px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
          {category.name}
          <span className="block text-primary-500 mt-1 text-2xl md:text-3xl">翻车现场</span>
        </h1>
        <p className="text-foreground-500 mt-4 max-w-2xl text-sm md:text-base">
          {category.description}
        </p>
      </section>

      {/* Article Grid */}
      <section className="px-4 md:px-8 lg:px-12 pb-12 md:pb-20">
        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative h-[200px] md:h-[220px] overflow-hidden bg-background-100">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {article.isExclusive && (
                    <span className="absolute top-3 left-3 px-2 py-1 bg-primary-500 text-background-50 text-xs font-bold rounded">
                      独家
                    </span>
                  )}
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-400 mb-2">
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{formatViews(article.views)} 阅读</span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                    {article.summary}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-foreground-400 text-lg">该分类暂无文章</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-4 text-primary-600 hover:underline"
            >
              返回首页
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        )}
      </section>

      {/* About Module for Category */}
      <section className="bg-background-100 py-12 md:py-20">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center">
            <div className="lg:w-1/2">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Investigative%20journalist%20desk%20with%20documents%20photos%20and%20red%20string%20connecting%20clues%20on%20dark%20wall%2C%20dramatic%20moody%20lighting%2C%20cinematic%20noir%20style%2C%20red%20and%20black%20color%20palette%2C%20high%20detail&width=800&height=500&seq=category-about&orientation=landscape"
                  alt="深度追踪"
                  className="w-full h-[280px] md:h-[360px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:w-1/2">
              <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-4">
                深度追踪
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                扒光真相
                <br />
                <span className="text-primary-500">不留死角</span>
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                91吃瓜网的每一篇{category.name}报道都经过至少三个独立信源的交叉验证。
                我们不满足于表面的八卦传播，而是深入挖掘事件背后的利益链条、人物关系与社会影响。
                从明星税务问题到网红造假产业链，我们只做有深度的爆料，不做无营养的流言。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all"
              >
                关注该分类
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ctaImage}
            alt="投稿征集"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div className="md:w-1/2">
              <h2 className="text-white font-heading text-2xl md:text-3xl lg:text-4xl font-black mb-6 md:mb-8">
                一起搞个大新闻
              </h2>
              <Link
                to="/about"
                className="inline-flex items-center gap-3 bg-white text-foreground-950 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                爆料有奖
                <span className="w-8 h-8 rounded-full bg-foreground-950 text-white flex items-center justify-center">
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </span>
              </Link>
            </div>
            <div className="md:w-1/2 md:text-right">
              <p className="text-white/80 text-sm md:text-base leading-relaxed">
                {category.name}独家爆料，一经核实发布即享现金奖励
              </p>
              <p className="text-white/80 text-sm md:text-base leading-relaxed mt-2">
                您的每一条线索，都可能揭开一个惊天大瓜
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}